import {
  Card,
  Col,
  Row,
  Typography,
  Input,
  Table,
  Pagination,
  Select,
  Tag,
  Button,
  Space,
} from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { SearchOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MongerlabCustomerService from "../services/MongerlabCustomerService";
import { IMongerlabCustomer } from "../types/MongerlabCustomer";
import { convertDate, openNotification, can } from "../utils";
import Filter, { FilterHandle } from "../components/MongerlabCustomer/Filter";
import { CopyClipboard } from "../components/General/CopyClipboard";
import { StartCall } from "../components/General/StartCall";
import _ from "lodash";

const { Title } = Typography;

const QUICK_FILTERS = [
  { key: "active-users", label: "Active Users", query: "&appStatusId=1" },
  { key: "suspended-users", label: "Suspended Users", query: "&appStatusId=2" },
  { key: "yeniden-aranacak", label: "Yeniden Aranacak", query: "&crmStatusId=4" },
  { key: "cevapsiz", label: "Cevapsız", query: "&crmStatusId=2" },
  { key: "bilgi-bekleniyor", label: "Bilgi Bekleniyor", query: "&crmStatusId=5" },
  { key: "geri-dondu", label: "Geri Döndürüldü", query: "&crmStatusId=6" },
] as const;

export const MongerlabCustomerList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const filterRef = useRef<FilterHandle>(null);
  const lastCurrentPage = location.state?.currentPage ?? 0;
  const lastPageSize = location.state?.pageSize ?? 20;
  const lastFilterText = location.state?.filterText ?? "";
  const lastSortByField = location.state?.sortByField ?? "registerDate";
  const lastSortByOrder = location.state?.sortByOrder ?? "desc";
  const lastSearchText = location.state?.searchText ?? "";

  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(lastSearchText);
  const [searchInputKey, setSearchInputKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(lastCurrentPage);
  const [pageSize, setPageSize] = useState(lastPageSize);
  const [sortByField, setSortByField] = useState(lastSortByField);
  const [sortByOrder, setSortByOrder] = useState(lastSortByOrder);
  const [customers, setCustomers] = useState<IMongerlabCustomer[]>([]);
  const [filterQuery, setFilterQuery] = useState(lastFilterText);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);
  const [crmStatusOptions, setCrmStatusOptions] = useState<any[]>([]);
  const [updatingStatus, setUpdatingStatus] = useState<Record<number, boolean>>({});

  const buildParam = useCallback(() => {
    const searchParam = searchText ? `&search=${encodeURIComponent(searchText)}` : "";
    return `?page=${currentPage}&size=${pageSize}&sort=${sortByField}&direction=${sortByOrder}${filterQuery}${searchParam}`;
  }, [currentPage, pageSize, sortByField, sortByOrder, filterQuery, searchText]);

  const loadCustomers = useCallback(() => {
    setLoading(true);
    const param = buildParam();
    localStorage.setItem("mongerlabCustomerFilterText", param);
    MongerlabCustomerService.getAll(param)
      .then((response) => {
        setCustomers(response.data.data);
        setTotalItems(response.data.totalElements);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [buildParam]);

  const canUpdateCrmStatus = can("mongerlab-customers:crm-status:update");

  useEffect(() => {
    if (!canUpdateCrmStatus) return;
    MongerlabCustomerService.getCrmStatuses().then((r) =>
      setCrmStatusOptions(r.data.map((i) => ({ value: i.id, label: i.name })))
    );
  }, [canUpdateCrmStatus]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const debouncedSearch = useCallback(
    _.debounce((value: string) => {
      setSearchText(value);
      setCurrentPage(0);
    }, 500),
    []
  );

  const applyQuickFilter = (key: string, query: string, label: string) => {
    if (activeQuickFilter === key) {
      setActiveQuickFilter(null);
      setFilterQuery("");
      filterRef.current?.resetForm();
    } else {
      setActiveQuickFilter(key);
      setFilterQuery(query);
      filterRef.current?.resetForm();
    }
    setCurrentPage(0);
  };

  const handleDetailedFilterChange = (query: string, fromDetailed?: boolean) => {
    setFilterQuery(query);
    setCurrentPage(0);
    if (fromDetailed) {
      setActiveQuickFilter(null);
    }
  };

  const handleClearAllFilters = () => {
    setActiveQuickFilter(null);
    setFilterQuery("");
    setSearchText("");
    setSearchInputKey((k) => k + 1);
    setCurrentPage(0);
  };

  const handleChangeCrmStatus = (id: number, crmStatusId: number) => {
    setUpdatingStatus((prev) => ({ ...prev, [id]: true }));
    MongerlabCustomerService.updateCrmStatus(id, { crmStatusId })
      .then(() => {
        openNotification("success", "Success", "CRM Status updated");
        loadCustomers();
      })
      .catch(console.error)
      .finally(() => setUpdatingStatus((prev) => ({ ...prev, [id]: false })));
  };

  const handleTableChange: TableProps<IMongerlabCustomer>["onChange"] = (_pagination, _filters, sorter: any) => {
    if (sorter?.field) {
      setSortByField(sorter.field);
      setSortByOrder(sorter.order === "ascend" ? "asc" : "desc");
    }
  };

  const columns: ColumnsType<IMongerlabCustomer> = [
    {
      title: "Customer",
      key: "name",
      dataIndex: "name",
      sorter: true,
      fixed: "left",
      width: 260,
      render: (_, row) => (
        <div>
          <Button
            type="link"
            style={{ padding: 0, height: "auto" }}
            onClick={() =>
              navigate(`/mongerlab-customers/detail/${row.id}`, {
                state: {
                  currentPage,
                  pageSize,
                  filterText: filterQuery,
                  sortByField,
                  sortByOrder,
                  searchText,
                },
              })
            }
          >
            {row.name}
          </Button>
          <div>
            <CopyClipboard text={row.email} />
          </div>
          <div>
            {row.phone || "-"} <StartCall text={row.phone || ""} />
          </div>
        </div>
      ),
    },
    { title: "App ID", dataIndex: "appId", key: "appId", sorter: true, width: 90 },
    { title: "Role", dataIndex: "roleName", key: "roleId", sorter: true, width: 140 },
    {
      title: "CRM Status",
      dataIndex: "crmStatusName",
      key: "crmStatusId",
      width: 200,
      render: (_, row) =>
        canUpdateCrmStatus ? (
          <Select
            value={row.crmStatusId}
            options={crmStatusOptions}
            style={{ width: "100%", minWidth: 180 }}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 260 }}
            size="small"
            loading={updatingStatus[row.id]}
            disabled={updatingStatus[row.id]}
            onChange={(value) => handleChangeCrmStatus(row.id, value)}
            showSearch
            optionFilterProp="label"
          />
        ) : (
          row.crmStatusName || "-"
        ),
    },
    { title: "App Status", dataIndex: "appStatusName", key: "appStatusId", sorter: true, width: 120 },
    { title: "Plan", dataIndex: "subsPlan", key: "subsPlan", sorter: true, width: 120 },
    { title: "Qty", dataIndex: "productQuantity", key: "productQuantity", sorter: true, width: 70 },
    {
      title: "Register Date",
      dataIndex: "registerDate",
      key: "registerDate",
      sorter: true,
      width: 150,
      render: (v) => convertDate(v),
    },
    {
      title: "Last Login",
      dataIndex: "lastLoginDate",
      key: "lastLoginDate",
      sorter: true,
      width: 150,
      render: (v) => (v ? convertDate(v) : "-"),
    },
    {
      title: "Accounts",
      dataIndex: "userAccountCount",
      key: "userAccountCount",
      sorter: true,
      width: 90,
    },
    {
      title: "Last Sync",
      dataIndex: "lastSyncedAt",
      key: "lastSyncedAt",
      sorter: true,
      width: 150,
      render: (v) => (v ? convertDate(v) : "-"),
    },
    {
      title: "Deleted",
      dataIndex: "isDeleted",
      key: "isDeleted",
      width: 90,
      render: (v) => (v ? <Tag color="red">Yes</Tag> : <Tag color="green">No</Tag>),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Mongerlab Customers
          </Title>
        </Col>
        <Col>
          <Input
            key={searchInputKey}
            prefix={<SearchOutlined />}
            placeholder="Search name, email or phone"
            defaultValue={lastSearchText}
            onChange={(e) => debouncedSearch(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 12 }}>
        {QUICK_FILTERS.map((preset) => (
          <Button
            key={preset.key}
            type={activeQuickFilter === preset.key ? "primary" : "default"}
            onClick={() => applyQuickFilter(preset.key, preset.query, preset.label)}
          >
            {preset.label}
          </Button>
        ))}
      </Space>

      <Card>
        <Row style={{ marginBottom: 12 }}>
          <Col>
            <Filter
              ref={filterRef}
              setFilterOptions={() => {}}
              onChangeFilter={handleDetailedFilterChange}
              onClearAll={handleClearAllFilters}
            />
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={customers}
          loading={loading}
          pagination={false}
          sticky
          scroll={{ x: 1400, y: "calc(100vh - 380px)" }}
          onChange={handleTableChange}
        />

        <Pagination
          style={{ marginTop: 16, textAlign: "right" }}
          current={currentPage + 1}
          pageSize={pageSize}
          total={totalItems}
          showSizeChanger
          onChange={(page, size) => {
            setCurrentPage(page - 1);
            setPageSize(size);
          }}
        />
      </Card>
    </div>
  );
};

export default MongerlabCustomerList;
